import { useCallback, useEffect, useRef, useState } from 'react';
import { Headset, Loader2, Send, Mail, Phone, Clock } from 'lucide-react';
import { supportApi, type SupportTicket } from '../api/support.api';
import AccessibleDialog from '../../../app/components/AccessibleDialog';
import { useCoreContext } from '../../../lib/coreContext';
import { SUPPORT_CONTACTS, supportPhoneTelHref } from '../lib/supportContacts';

interface SupportTicketDrawerProps {
  open: boolean;
  onClose: () => void;
  notify?: (message: string, variant?: 'info' | 'success' | 'warning' | 'error') => void;
}

const CONTACTS = {
  support: SUPPORT_CONTACTS.supportEmail,
  billing: SUPPORT_CONTACTS.billingEmail,
  orders: SUPPORT_CONTACTS.ordersEmail,
  bottlePickUps: SUPPORT_CONTACTS.bottlePickUpsEmail,
  hours: SUPPORT_CONTACTS.hours
};

export default function SupportTicketDrawer({ open, onClose, notify }: SupportTicketDrawerProps) {
  const core = useCoreContext();
  const isLoggedIn = !!core.currentUser;

  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [replyBody, setReplyBody] = useState('');
  const [activeTicketId, setActiveTicketId] = useState('');
  const [creating, setCreating] = useState(false);
  const [replying, setReplying] = useState(false);
  const creatingRef = useRef(false);
  const replyingRef = useRef(false);

  const busy = creating || replying;
  const requestClose = () => {
    if (creatingRef.current || replyingRef.current) return;
    onClose();
  };

  const refresh = useCallback(async () => {
    if (!isLoggedIn) return;
    setLoading(true);
    setLoadError(null);
    try {
      setTickets(await supportApi.listMine());
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to load support tickets.';
      setLoadError(message);
      notify?.(message, 'warning');
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn, notify]);

  useEffect(() => {
    if (open) void refresh();
  }, [open, refresh]);

  const activeTicket = tickets.find((ticket) => ticket._id === activeTicketId) || null;

  return (
    <AccessibleDialog
      open={open}
      onClose={requestClose}
      title="Support Tickets"
      description="Manage and reply to your customer support tickets."
      variant="drawer-right"
      renderChrome={false}
      ariaLabelledBy="support-drawer-title"
      closeOnEscape={!busy}
      closeOnBackdrop={!busy}
    >
      <div className="p-5 space-y-4 max-h-screen overflow-y-auto">
        <div className="flex items-center justify-between pb-2 border-b border-white/5">
          <div className="flex items-center gap-2">
            <Headset className="w-4 h-4 text-ninpo-lime" aria-hidden="true" />
            <h2 id="support-drawer-title" className="text-xs font-black uppercase tracking-widest text-white">
              Chat with a teammate
            </h2>
          </div>
          <button
            type="button"
            onClick={requestClose}
            disabled={busy}
            aria-label="Close support drawer"
            className="text-slate-400 hover:text-white text-sm disabled:opacity-40"
          >
            ✕
          </button>
        </div>

        {isLoggedIn ? (
          <>
            <div className="rounded-2xl border border-white/10 p-4 space-y-2 bg-white/[0.01]">
              <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">Create Support Ticket</h3>
              <label htmlFor="support-subject" className="sr-only">
                Subject
              </label>
              <input
                id="support-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Subject"
                className="w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-ninpo-lime text-white placeholder-slate-500 transition-colors"
              />
              <label htmlFor="support-body" className="sr-only">
                How can we help?
              </label>
              <textarea
                id="support-body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="How can we help?"
                className="w-full min-h-20 rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-ninpo-lime text-white placeholder-slate-500 transition-colors"
              />
              <button
                type="button"
                disabled={creating || !subject.trim() || !body.trim()}
                onClick={async () => {
                  if (creatingRef.current) return;
                  creatingRef.current = true;
                  setCreating(true);
                  try {
                    await supportApi.create({ subject, body });
                    setSubject('');
                    setBody('');
                    notify?.('Support ticket created.', 'success');
                    await refresh();
                  } catch (error) {
                    notify?.(error instanceof Error ? error.message : 'Could not create support ticket.', 'warning');
                  } finally {
                    creatingRef.current = false;
                    setCreating(false);
                  }
                }}
                className="w-full rounded-xl bg-ninpo-lime text-ninpo-black py-2.5 text-[10px] font-black uppercase tracking-widest hover:bg-white hover:text-black transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {creating ? 'Creating…' : 'Create Ticket'}
              </button>
            </div>

            {loading ? <Loader2 className="w-5 h-5 animate-spin text-slate-500 mx-auto py-2" /> : null}

            <div className="space-y-2">
              <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">Your Tickets</h3>
              {tickets.map((ticket) => (
                <button
                  key={ticket._id}
                  onClick={() => setActiveTicketId(ticket._id)}
                  className={`w-full text-left rounded-xl border p-3 transition-all ${
                    activeTicketId === ticket._id
                      ? 'border-ninpo-lime bg-ninpo-lime/5 shadow-inner'
                      : 'border-white/10 bg-white/5 hover:border-white/20'
                  }`}
                >
                  <p className="text-xs font-black uppercase tracking-widest text-white">{ticket.subject}</p>
                  <p className="text-[9px] uppercase tracking-widest text-slate-500 mt-1">Status: {ticket.status}</p>
                </button>
              ))}
              {loadError ? (
                <div className="space-y-2 py-2 text-center">
                  <p className="text-[10px] uppercase tracking-widest text-ninpo-red">{loadError}</p>
                  <button
                    type="button"
                    onClick={() => void refresh()}
                    className="text-[10px] font-black uppercase tracking-widest text-ninpo-lime hover:underline"
                  >
                    Retry
                  </button>
                </div>
              ) : !loading && tickets.length === 0 ? (
                <p className="text-[10px] uppercase tracking-widest text-slate-500 text-center py-2">No tickets submitted yet.</p>
              ) : null}
            </div>

            {activeTicket ? (
              <div className="rounded-2xl border border-white/10 p-4 space-y-3 bg-white/[0.01]">
                <div className="flex items-center justify-between border-b border-white/5 pb-2">
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 truncate">Ticket Message History</p>
                  <button
                    onClick={() => setActiveTicketId('')}
                    className="text-[9px] font-black uppercase tracking-widest text-ninpo-red hover:underline"
                  >
                    Close Thread
                  </button>
                </div>
                <div className="max-h-52 overflow-y-auto space-y-2 pr-1 scrollbar-thin scrollbar-thumb-zinc-700 scrollbar-track-transparent">
                  {activeTicket.messages.map((message, idx) => {
                    const isStaff = message.authorRole === 'staff';
                    return (
                      <div
                        key={`${message.createdAt}-${idx}`}
                        className={`rounded-xl px-3 py-2 text-xs flex flex-col ${
                          isStaff
                            ? 'bg-ninpo-lime/10 border border-ninpo-lime/10 text-slate-200'
                            : 'bg-white/5 border border-white/5 text-slate-300'
                        }`}
                      >
                        <span className="text-[8px] font-black uppercase tracking-widest text-slate-500 mb-1">
                          {isStaff ? 'Ninpo Support Staff' : 'You (Customer)'}
                        </span>
                        <p className="leading-relaxed">{message.body}</p>
                      </div>
                    );
                  })}
                </div>
                <div className="flex gap-2">
                  <label htmlFor="support-reply" className="sr-only">
                    Reply to ticket
                  </label>
                  <input
                    id="support-reply"
                    value={replyBody}
                    onChange={(e) => setReplyBody(e.target.value)}
                    placeholder="Type reply..."
                    className="flex-1 rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-ninpo-lime text-white placeholder-slate-500"
                  />
                  <button
                    type="button"
                    disabled={replying || !replyBody.trim()}
                    onClick={async () => {
                      if (replyingRef.current || !replyBody.trim()) return;
                      replyingRef.current = true;
                      setReplying(true);
                      try {
                        const updated = await supportApi.replyAsCustomer(activeTicket._id, replyBody);
                        if (!updated?._id) {
                          notify?.('Reply was not saved. Try again.', 'warning');
                          return;
                        }
                        setReplyBody('');
                        await refresh();
                      } catch (error) {
                        notify?.(error instanceof Error ? error.message : 'Could not send reply.', 'warning');
                      } finally {
                        replyingRef.current = false;
                        setReplying(false);
                      }
                    }}
                    aria-label="Send reply"
                    className="px-3 rounded-xl bg-white/10 border border-white/10 hover:bg-ninpo-lime hover:text-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send className="w-4 h-4 text-white hover:text-inherit" aria-hidden="true" />
                  </button>
                </div>
              </div>
            ) : null}
          </>
        ) : (
          <div className="rounded-2xl border border-white/5 bg-white/[0.01] p-5 text-center space-y-3 shadow-inner">
            <div className="w-10 h-10 bg-white/5 rounded-full flex items-center justify-center mx-auto border border-white/5">
              <Headset className="w-5 h-5 text-slate-500" />
            </div>
            <p className="text-xs text-slate-300 font-bold uppercase tracking-widest">Browsing as Guest</p>
            <p className="text-[10px] text-slate-500 uppercase tracking-widest leading-relaxed">
              Use the chat assistant first. Sign in to open a ticket chat thread with a teammate.
            </p>
          </div>
        )}

        {/* Phone/email only as last resort — collapsed so chat + tickets stay primary */}
        <details className="rounded-2xl border border-white/5 bg-white/[0.02] p-3">
          <summary className="cursor-pointer text-[10px] font-black uppercase tracking-widest text-zinc-500 hover:text-zinc-300 list-none [&::-webkit-details-marker]:hidden">
            Still need phone or email?
          </summary>
          <div className="mt-3 flex flex-col gap-2">
            <a
              href={`mailto:${CONTACTS.support}`}
              className="flex items-center gap-2.5 p-2 rounded-xl bg-white/5 hover:bg-white/10 hover:text-ninpo-lime transition-all border border-white/5"
            >
              <Mail className="w-3.5 h-3.5 text-ninpo-lime flex-shrink-0" />
              <div className="flex flex-col min-w-0">
                <span className="text-[8px] font-black text-slate-500 uppercase tracking-widest leading-none">General Support</span>
                <span className="text-[11px] font-bold text-white transition-colors truncate mt-1">{CONTACTS.support}</span>
              </div>
            </a>
            <a
              href={`mailto:${CONTACTS.billing}`}
              className="flex items-center gap-2.5 p-2 rounded-xl bg-white/5 hover:bg-white/10 hover:text-ninpo-lime transition-all border border-white/5"
            >
              <Mail className="w-3.5 h-3.5 text-ninpo-lime flex-shrink-0" />
              <div className="flex flex-col min-w-0">
                <span className="text-[8px] font-black text-slate-500 uppercase tracking-widest leading-none">Billing &amp; Refunds</span>
                <span className="text-[11px] font-bold text-white transition-colors truncate mt-1">{CONTACTS.billing}</span>
              </div>
            </a>
            <a
              href={`mailto:${CONTACTS.orders}`}
              className="flex items-center gap-2.5 p-2 rounded-xl bg-white/5 hover:bg-white/10 hover:text-ninpo-lime transition-all border border-white/5"
            >
              <Mail className="w-3.5 h-3.5 text-ninpo-lime flex-shrink-0" />
              <div className="flex flex-col min-w-0">
                <span className="text-[8px] font-black text-slate-500 uppercase tracking-widest leading-none">Order Inquiries</span>
                <span className="text-[11px] font-bold text-white transition-colors truncate mt-1">{CONTACTS.orders}</span>
              </div>
            </a>
            <a
              href={`mailto:${CONTACTS.bottlePickUps}`}
              className="flex items-center gap-2.5 p-2 rounded-xl bg-white/5 hover:bg-white/10 hover:text-ninpo-lime transition-all border border-white/5"
            >
              <Mail className="w-3.5 h-3.5 text-ninpo-lime flex-shrink-0" />
              <div className="flex flex-col min-w-0">
                <span className="text-[8px] font-black text-slate-500 uppercase tracking-widest leading-none">Bottle &amp; Can Pickups</span>
                <span className="text-[11px] font-bold text-white transition-colors truncate mt-1">{CONTACTS.bottlePickUps}</span>
              </div>
            </a>
            <div className="grid grid-cols-2 gap-2">
              <a
                href={supportPhoneTelHref}
                className="flex items-center gap-2 p-2 rounded-xl bg-white/5 hover:bg-white/10 hover:text-ninpo-lime transition-all border border-white/5"
              >
                <Phone className="w-3.5 h-3.5 text-ninpo-lime flex-shrink-0" />
                <div className="flex flex-col min-w-0">
                  <span className="text-[8px] font-black text-slate-500 uppercase tracking-widest leading-none">Phone Support</span>
                  <span className="text-[10px] font-bold text-white transition-colors truncate mt-1">{SUPPORT_CONTACTS.phoneDisplay}</span>
                </div>
              </a>
              <div className="flex items-center gap-2 p-2 rounded-xl bg-white/5 border border-white/5">
                <Clock className="w-3.5 h-3.5 text-ninpo-lime flex-shrink-0" />
                <div className="flex flex-col">
                  <span className="text-[8px] font-black text-slate-500 uppercase tracking-widest leading-none">Operating Hours</span>
                  <span className="text-[9px] font-bold text-slate-300 mt-1">Open 24/7 (Store availability)</span>
                </div>
              </div>
            </div>
            <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest leading-relaxed">
              Hours: {CONTACTS.hours}. Include your Order ID when emailing. Same-day delivery issues must be reported the day of delivery.
            </p>
          </div>
        </details>
      </div>
    </AccessibleDialog>
  );
}
